import Toggle from "@/ui/Toggle";
import SeccionLayout, { PieGuardar } from "../SeccionLayout";
import { AYUDA_SECCION } from "@/components/ayuda/vistas/configuracion";
import { useAppSettings, useBorradorSeccion } from "../useAppSettings";

/**
 * La perilla GLOBAL del buzón: cada cuánto se revisan los buzones del equipo.
 *
 * Ojo con el reparto, que es a propósito: aquí vive lo que aplica a todos y solo
 * un admin puede tocar. **El interruptor de CADA persona vive en su Perfil**,
 * pegado a sus credenciales de correo — es el mismo reparto que ya existe para
 * el SMTP: Configuración pone las reglas, Perfil pone las llaves.
 *
 * Sigue la regla de guardado de la casa: los switches se aplican al momento,
 * los números esperan al botón.
 */
export default function Buzon() {
  const { settings, cargando, guardando, guardar, alternar } = useAppSettings();
  const { valores, set, sucio } = useBorradorSeccion(settings, (s) => ({
    buzon_radar_minutos: s.buzon_radar_minutos,
    buzon_radar_hora_inicio: s.buzon_radar_hora_inicio,
    buzon_radar_hora_fin: s.buzon_radar_hora_fin,
  }));

  if (cargando || !settings || !valores) {
    return <p className="text-sm text-zinc-500">Cargando…</p>;
  }

  return (
    <SeccionLayout
      ayuda={AYUDA_SECCION["buzon"]}
      ancho="formulario"
      titulo="Buzón"
      descripcion="ArkTickets puede asomarse al correo empresarial del equipo para avisar qué puede servir. Lo hace en solo lectura: nunca marca como leído, ni mueve, ni borra nada. Lo que responde a un caso que ya existe entra solo; lo demás espera a que alguien lo revise."
    >
      <div className="card space-y-2">
        <Toggle
          checked={settings.buzon_radar_activo}
          onChange={(v) =>
            void alternar(
              "buzon_radar_activo",
              v,
              v ? "Revisión del buzón activada" : "Revisión del buzón desactivada",
            )
          }
          label="Revisar los buzones automáticamente"
        />
        <p className="text-[11px] text-zinc-500">
          Se aplica al momento. Apagarlo no quita el botón «Revisar mi correo» de la Bandeja: ese
          es a petición y siempre funciona.
        </p>
      </div>

      <div className="card space-y-3">
        <p className="text-xs text-zinc-400">Cada cuánto y en qué horario.</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="mb-1 block text-xs text-zinc-400">Cada (minutos)</span>
            <input
              type="number"
              min={5}
              max={240}
              className="input"
              value={valores.buzon_radar_minutos}
              onChange={(e) => set({ buzon_radar_minutos: Number(e.target.value) })}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-zinc-400">Desde (hora)</span>
            <input
              type="number"
              min={0}
              max={23}
              className="input"
              value={valores.buzon_radar_hora_inicio}
              onChange={(e) => set({ buzon_radar_hora_inicio: Number(e.target.value) })}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-zinc-400">Hasta (hora)</span>
            <input
              type="number"
              min={1}
              max={24}
              className="input"
              value={valores.buzon_radar_hora_fin}
              onChange={(e) => set({ buzon_radar_hora_fin: Number(e.target.value) })}
            />
          </label>
        </div>
        <p className="text-[11px] text-zinc-500">
          Solo de lunes a viernes. Fuera de ese horario no revisa: nadie va a atender un correo a
          las tres de la mañana, y una conexión de más al servidor de correo no le sirve a nadie.
        </p>
        <PieGuardar
          sucio={sucio}
          guardando={guardando}
          onGuardar={() => void guardar(valores, "Horario del buzón guardado")}
          textoGuardar="Guardar el horario"
        />
      </div>

      <div className="card space-y-2">
        <Toggle
          checked={settings.buzon_ia_activa}
          onChange={(v) =>
            void alternar(
              "buzon_ia_activa",
              v,
              v ? "IA del buzón activada" : "IA del buzón desactivada",
            )
          }
          label="Usar IA para lo que las reglas no alcanzan a clasificar"
        />
        <p className="text-[11px] text-zinc-500">
          Las reglas del sistema (remitentes conocidos, folios, IMEIs, palabras clave) resuelven la
          mayoría <strong>sin que nada salga del servidor</strong>. Esto es la red para el cliente
          nuevo que escribe por primera vez desde una dirección que nadie conoce: de esos correos
          —y solo de esos— viajan <strong>el título y el remitente</strong>. Nunca el contenido,
          nunca los archivos.
        </p>
      </div>
    </SeccionLayout>
  );
}
